import { Box, Chip, Stack, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Shared status-display primitives for the transaction tabs (Redemption, Dispense, ...).
 * Kept in one place so an icon/badge/banner looks and sizes identically no matter which
 * tab renders it, rather than each tab hand-rolling its own chip/box variant.
 */

// Shared size for every status icon across these tabs so nothing renders at a different
// scale or sits off-center relative to the others.
export const ICON_SIZE = 24;

export const ERROR_LIGHT_BG = '#FDECEA';
export const WARNING_LIGHT_BG = '#FFF4E5';
export const SUCCESS_LIGHT_BG = '#EDF7ED';

// The dark "hero" card background used for the Redemption tab's Goldbacks-to-redeem card and
// the Dispense tab's stat summary card, so both render the exact same navy, not just similar.
export const DARK_CARD_BG = '#0F172A';

export const DarkStatColumn = ({
  label,
  value,
  color = '#fff',
}: {
  label: string;
  value: ReactNode;
  color?: string;
}) => (
  <Box textAlign="center">
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }} display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="bold" sx={{ color }}>
      {value}
    </Typography>
  </Box>
);

export const IconBadge = ({
  icon,
  color,
  size = ICON_SIZE,
}: {
  icon: ReactNode;
  color: string;
  size?: number;
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color,
      flexShrink: 0,
      fontSize: size,
      lineHeight: 1,
    }}
  >
    {icon}
  </Box>
);

export const Banner = ({
  icon,
  title,
  description,
  color,
  backgroundColor,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  color: string;
  backgroundColor: string;
}) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="flex-start"
    sx={{ p: 2, borderRadius: '12px', backgroundColor, mb: 2 }}
  >
    <IconBadge icon={icon} color={color} />
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" color={color}>
        {title}
      </Typography>
      <Typography variant="body2" color={color}>
        {description}
      </Typography>
    </Box>
  </Stack>
);

export const DetailRow = ({
  label,
  value,
  emphasize,
  valueColor,
  last,
  wrap,
}: {
  label: string;
  value: ReactNode;
  emphasize?: boolean;
  valueColor?: string;
  last?: boolean;
  wrap?: boolean;
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    gap={2}
    sx={{
      py: 1.5,
      borderBottom: last ? 'none' : '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography
      variant="body2"
      color={emphasize ? 'error.main' : 'text.secondary'}
      fontWeight={emphasize ? 700 : 400}
      flexShrink={0}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      color={emphasize ? 'error.main' : (valueColor ?? 'text.primary')}
      fontWeight={emphasize ? 700 : 600}
      textAlign="right"
      sx={wrap ? { minWidth: 0, wordBreak: 'break-all' } : undefined}
    >
      {value}
    </Typography>
  </Stack>
);

// Matches the section-title convention used across the Case single view (e.g. CaseDetails'
// "Location Address" / "Resolution Summary" headers), so these tabs read consistently with the
// rest of the app instead of the smaller all-caps "overline" style they used before.
export const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitle2" fontWeight={600} color="text.primary" display="block" mb={1}>
    {children}
  </Typography>
);

/**
 * The one chip used across the redemption UI and the sale page, so every status/label chip
 * shares the same size, weight and shape. `fill` gives a solid custom colour with white text
 * (settlement statuses); otherwise MUI's `color`/`variant` apply as usual.
 */
export const StatusChip = ({ fill, sx, ...props }: ChipProps & { fill?: string }) => (
  <Chip
    size="small"
    {...props}
    sx={{
      height: 24,
      fontSize: '0.75rem',
      fontWeight: 600,
      borderRadius: '6px',
      ...(fill && { backgroundColor: fill, color: 'common.white' }),
      ...sx,
    }}
  />
);
